import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { findEntity } from '@common-reducers/backend-api/backend-api';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import type { CommonRootState } from '@common/types/common-root-state-type';
import {
    createAudiencePreviewStore,
    type AudiencePreviewTransport,
    type PreviewColumn,
    type PreviewRow,
} from './audience-preview-loader';
import { campaignDestination, type DestinationPlan } from './destination-plan';
import type { DestinationHints } from './destination-avatars';
import { selectAudienceMemberNames, selectAudiencePreviewIds } from './destination-selectors';

/** One preview cache for the page: every row and the hover card share it. */
export const audiencePreviewStore = createAudiencePreviewStore();

type Dispatch = ReturnType<typeof useAppDispatch>;

/**
 * Reads through RTK Query's `findEntity` — the transport the DB thunks use —
 * WITHOUT going through a DB thunk, so nothing lands in the tasks or columns
 * slices (see audience-preview-loader.ts for why that matters).
 */
async function find<T>(dispatch: Dispatch, service: string, query: Record<string, unknown>): Promise<T[]> {
    const sub = dispatch(findEntity.initiate({ service, query } as any, { forceRefetch: true }) as any) as any;
    try {
        const response = await sub.unwrap();
        return ((response && 'data' in response) ? response.data : response) ?? [];
    } finally {
        sub.unsubscribe?.();
    }
}

function transportFor(dispatch: Dispatch): AudiencePreviewTransport {
    return {
        // Same `field[$in][]` shape the DB thunks send.
        findColumns: boardIds => find<PreviewColumn>(dispatch, 'task-columns', {
            'boardId[$in][]': boardIds,
            type: 'whatsapp-id-cell',
            $select: ['_id', 'boardId', 'type', 'deleted'],
            $limit: 200,
        }),
        findRows: (boardIds, columnIds, limit) => find<PreviewRow>(dispatch, 'tasks', {
            'boardId[$in][]': boardIds,
            deleted: { $ne: true },
            archived: { $ne: true },
            // Only the column that holds the WhatsApp id, and the row's name for
            // the member list — not the whole row.
            $select: ['boardId', 'text', 'deleted', 'archived', ...columnIds.map(id => `customData.${id}`)],
            $sort: { _id: 1 },
            $limit: limit,
            $paginate: false,
        }),
    };
}

const toIds = (boardIDArray: ReadonlyArray<string | number | null | undefined> | null | undefined): string[] =>
    [...new Set((boardIDArray ?? []).map(id => String(id ?? '')).filter(Boolean))];

/**
 * WHO a campaign goes to: up to five faces and "+N", for the sidebar row and the
 * hover card alike. Faces come from audience rows already in the store when
 * another screen loaded them, else from the batched preview read above — so an
 * audience nobody has opened still shows its people, and still costs no request
 * per row.
 */
export function useCampaignDestination(
    campaign: { boardIDArray?: ReadonlyArray<string | number | null | undefined> | null } | null | undefined,
    recipientCount: number,
): DestinationPlan {
    const dispatch = useAppDispatch();
    const boardKey = toIds(campaign?.boardIDArray).join(',');
    const boardIds = useMemo(() => (boardKey ? boardKey.split(',') : []), [boardKey]);

    // Rows already in the store win: they are the freshest, and free.
    const storePreview = useAppSelector(selectAudiencePreviewIds);
    const missing = useMemo(() => boardIds.filter(id => !storePreview[id]), [boardIds, storePreview]);
    const missingKey = missing.join(',');

    // Each audience's size, so small ones can be read together (see the loader).
    const knownRows = useAppSelector(
        (state: CommonRootState) => Object.fromEntries(missing.map(id => [id, (state as any).DBBoardsReducer?.entities?.[id]?.tasksCount])),
    ) as Record<string, number | undefined>;

    useEffect(() => {
        if (!missingKey) return;
        audiencePreviewStore.request(missingKey.split(','), knownRows, transportFor(dispatch));
    }, [missingKey, knownRows, dispatch]);

    const version = useSyncExternalStore(audiencePreviewStore.subscribe, audiencePreviewStore.version, audiencePreviewStore.version);

    return useMemo(() => {
        if (!campaign) return { mode: 'none' };
        const merged: Record<string, readonly string[]> = {};
        for (const id of boardIds) {
            const ids = storePreview[id] ?? audiencePreviewStore.get(id);
            if (ids) merged[id] = ids;
        }
        return campaignDestination({ boardIDArray: boardIds }, merged, recipientCount);
        // `version` is the signal that a fetched preview landed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!campaign, boardIds, storePreview, version, recipientCount]);
}

/**
 * Names for a campaign's previewed members — the audience rows' own names, from
 * rows already in the store or from the same batched preview read the faces came
 * from. No request of its own; nothing here fetches.
 */
export function useAudienceMemberHints(plan: DestinationPlan): DestinationHints | undefined {
    const storeNames = useAppSelector(selectAudienceMemberNames);
    const version = useSyncExternalStore(audiencePreviewStore.subscribe, audiencePreviewStore.version, audiencePreviewStore.version);
    const key = plan.mode === 'group' ? plan.faces.filter(Boolean).join('|') : '';
    return useMemo(() => {
        if (!key) return undefined;
        const hints: Record<string, { name?: string }> = {};
        for (const id of key.split('|')) {
            const name = storeNames[id] ?? audiencePreviewStore.nameOf(id);
            if (name) hints[id] = { name };
        }
        return hints;
        // `version` is the signal that a fetched preview (and its names) landed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, storeNames, version]);
}
